import { EventEmitterInterface } from 'pf-frontend-common/dist/module/event/emitter.interface';

import { BackendConfigLanguageInterface } from 'common/data/backend/config/language.interface';
import { BackendConfigSettingsInterface } from 'common/data/backend/config/settings.interface';
import { PropertyCategoryIdentifierEnum } from 'common/data/property-category/identifier.enum';
import { UserFrontendLoginProvidersEnum } from 'common/data/user/frontend/login/providers.enum';
import { languageGetName } from 'common/helper/language/get-name';
import { propertyGetCategory } from 'common/helper/property/get-category';
import { settingsGetPageType } from 'common/helper/settings/get-page-type';
import { LeadSignInStatsGtmLabelEnum } from 'common/module/lead/sign-in/stats/gtm/label.enum';
import { StatsGtmProviderInterface } from 'common/module/stats/gtm/provider.interface';
import { StatsSnowplow2ActionEnum } from 'common/module/stats/snowplow2/action.enum';
import { StatsSnowplow2ProviderInterface } from 'common/module/stats/snowplow2/provider.interface';
import { UserAuthenticationEvent } from 'common/module/user/authentication.event';
import { UserAuthenticationPayloadInterface } from 'common/module/user/authentication-payload.interface';
import { UserPopupLoginStatusEnum } from 'common/module/user/popup/login-status.enum';
import { UserSignInStatsGtmFinishSignInWithEmail } from 'common/module/user/sign-in/stats/gtm/finish-signin-with-email';
import { UserSignInStatsGtmFinishSignInWithFacebook } from 'common/module/user/sign-in/stats/gtm/finish-signin-with-facebook';
import { UserSignInStatsGtmFinishSignInWithGoogle } from 'common/module/user/sign-in/stats/gtm/finish-signin-with-google';
import { UserSignInStatsGtmFinishSignUpWithEmail } from 'common/module/user/sign-in/stats/gtm/finish-signup-with-email';
import { UserSignInStatsGtmLogoutUser } from 'common/module/user/sign-in/stats/gtm/logout-user';
import { UserSignInStatsGtmStartSignInWithFacebook } from 'common/module/user/sign-in/stats/gtm/start-signin-with-facebook';
import { UserSignInStatsGtmStartSignInWithGoogle } from 'common/module/user/sign-in/stats/gtm/start-signin-with-google';
import { UserSignIn2IdEnum } from 'common/module/user/sign-in2/id.enum';
import { UserSignIn2InteractionEvent } from 'common/module/user/sign-in2/interaction.event';
import { UserAuthenticationServiceInterface } from 'common/service/user-authentication/service.interface';
import { UserSignInStatsServiceInterface } from 'common/service/user-sign-in-stats/service.interface';

export class UserSignInStatsStore implements UserSignInStatsServiceInterface {
  /**
   * Login popup caller type
   */
  protected statsGtmEventLabel: string;

  /**
   * Page category identifier for gtm stats
   */
  protected statsGtmPageCategoryIdentifier: PropertyCategoryIdentifierEnum;

  /**
   * Sign in2 interaction gtm event labels
   */
  private readonly signIn2EventLabels: Record<UserSignIn2IdEnum, LeadSignInStatsGtmLabelEnum> = {
    [UserSignIn2IdEnum.propertyCallLeadModal]: LeadSignInStatsGtmLabelEnum.postCallLead,
  };

  /**
   * Constructor
   */
  public constructor(
    private userAuthenticationService: UserAuthenticationServiceInterface,
    private statsGtmProvider: StatsGtmProviderInterface,
    private statsSnowplow2Service: StatsSnowplow2ProviderInterface,
    private languageSettings: BackendConfigLanguageInterface,
    private settings: BackendConfigSettingsInterface,
    private userSignIn2InteractionService: EventEmitterInterface
  ) {}

  /**
   * Init service
   */
  public initialize(): void {
    this.observers();
  }

  /**
   * @inheritdoc
   */
  public getGtmEventLabel(): string {
    return this.statsGtmEventLabel;
  }

  /**
   * @inheritdoc
   */
  public setGtmEventLabel(statsGtmEventLabel: string): void {
    this.statsGtmEventLabel = statsGtmEventLabel;
  }

  /**
   * @inheritDoc
   */
  public setPageCategoryIdentifier(category: PropertyCategoryIdentifierEnum): void {
    this.statsGtmPageCategoryIdentifier = category;
  }

  /**
   * Attach observers
   */
  private observers(): void {
    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.logOutSucceeded, this.onLogOutSucceeded);
    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.registrationSucceeded, this.onRegistrationSucceeded);
    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.signInSucceeded, this.onSignInSucceeded);
    this.userSignIn2InteractionService.addListener(
      UserSignIn2InteractionEvent.clickSignInFacebook,
      this.onClickSignInFacebookSignIn2
    );
    this.userSignIn2InteractionService.addListener(
      UserSignIn2InteractionEvent.clickSignInGoogle,
      this.onClickSignInGoogleSignIn2
    );
  }

  /**
   * Set user id for stats snowplow2
   */
  private statsSnowplow2SetUserId(userId: string): void {
    this.statsSnowplow2Service.send({
      action: StatsSnowplow2ActionEnum.setUserId,
      options: {
        userId,
      },
    });
  }

  /**
   * Log page view stat for logged in user
   */
  private statsGtmPageView(userId?: string): void {
    this.statsGtmProvider.send({
      language: languageGetName(this.languageSettings.current),
      pageCategory: propertyGetCategory(this.statsGtmPageCategoryIdentifier) || '',
      pageType: settingsGetPageType(this.settings),
      userId: userId || '',
      userStatus: userId ? UserPopupLoginStatusEnum.loggedIn : UserPopupLoginStatusEnum.notLoggedIn,
    });
  }

  /**
   * Handle click on sign in with facebook button interaction
   */
  private onClickSignInFacebookSignIn2 = (id: UserSignIn2IdEnum) => {
    this.setGtmEventLabel(settingsGetPageType(this.settings) + ' - ' + this.signIn2EventLabels[id]);
    // Log start signin with facebook
    this.statsGtmProvider.send(UserSignInStatsGtmStartSignInWithFacebook(this.getGtmEventLabel()));
  };

  /**
   * Handle click on sign in with google button interaction
   */
  private onClickSignInGoogleSignIn2 = (id: UserSignIn2IdEnum) => {
    this.setGtmEventLabel(settingsGetPageType(this.settings) + ' - ' + this.signIn2EventLabels[id]);
    // Log start signin with google
    this.statsGtmProvider.send(UserSignInStatsGtmStartSignInWithGoogle(this.getGtmEventLabel()));
  };

  /**
   * Sign in succeeded
   */
  private onSignInSucceeded = (output: UserAuthenticationPayloadInterface) => {
    switch (output.provider) {
      case UserFrontendLoginProvidersEnum.google:
        // Log sign in with google gtm stat
        this.statsGtmProvider.send(UserSignInStatsGtmFinishSignInWithGoogle(this.statsGtmEventLabel));
        break;

      case UserFrontendLoginProvidersEnum.facebook:
        // Log sign in with facebook gtm stat
        this.statsGtmProvider.send(UserSignInStatsGtmFinishSignInWithFacebook(this.statsGtmEventLabel));
        break;

      case UserFrontendLoginProvidersEnum.email:
        // Log sign in with email gtm stat
        this.statsGtmProvider.send(UserSignInStatsGtmFinishSignInWithEmail(this.statsGtmEventLabel));
        break;
    }

    // Set user id for snowplow2 stats
    this.statsSnowplow2SetUserId(output.userId);

    // Log page view stat for logged in user
    this.statsGtmPageView(output.userId);
  };

  /**
   * Registration succeeded
   */
  private onRegistrationSucceeded = (output: UserAuthenticationPayloadInterface) => {
    // Log sign in with email gtm stat
    this.statsGtmProvider.send(UserSignInStatsGtmFinishSignUpWithEmail(this.statsGtmEventLabel));

    // Set user id for snowplow2 stats
    this.statsSnowplow2SetUserId(output.userId);

    // Log page view stat for logged in user
    this.statsGtmPageView(output.userId);
  };

  /**
   * On logout
   */
  private onLogOutSucceeded = (output: UserAuthenticationPayloadInterface) => {
    // Log sign in gtm stat
    this.statsGtmProvider.send(UserSignInStatsGtmLogoutUser(settingsGetPageType(this.settings)));

    this.statsSnowplow2SetUserId(output.userId);

    // Log page view stat for logged in user
    this.statsGtmPageView();
  };
}
